// src/screens/CartsByStoreScreen.tsx
import React, { useMemo, useState, useEffect } from "react";
import {
  View,
  Text,
  StyleSheet,
  Image,
  TouchableOpacity,
  ScrollView,
  Modal,
  Pressable,
  Platform,
  ActionSheetIOS,
} from "react-native";
import { platformAlert } from "../utils/platformAlert";
import { Feather } from "@expo/vector-icons";
import { useNavigation, useFocusEffect } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";

import { useCart } from "../hooks/useCart";
import { useColors } from "../hooks/useColors";
import { useDeliveryAddressContext } from "../contexts/DeliveryAddressContext";
import { supabase } from "../lib/supabase";
import { useAuth } from "../hooks/useAuth";
import type { RootStackParamList } from "../types/navigation";
import { formatPrice } from "../utils/format";
import { useVendorHoursRealtime } from "../hooks/useVendorHoursRealtime";
import { useIsFocused } from "@react-navigation/native";
import { spacing, typography } from "../theme";

type Nav = NativeStackNavigationProp<RootStackParamList>;

type GroupedStore = {
  vendorId: string;
  storeName: string;
  addressLine1?: string;
  city?: string;
  state?: string;
  itemsCount: number;
  subtotal: number;
  thumb?: string | null;
  logo_image?: string | null; // ← добавили в тип
};

export default function CartsByStoreScreen() {
  const { cartItems, removeItem, refreshCart } = useCart();
  const { formattedAddress } = useDeliveryAddressContext();
  const { session } = useAuth();
  const colors = useColors();
  const styles = createStyles(colors);
  const nav = useNavigation<Nav>();
  const isFocused = useIsFocused();

  console.log("Rendering CartsByStoreScreen with cartItems:", cartItems);

  // Get unique vendor IDs from cart items for realtime monitoring
  const cartVendorIds = useMemo(() => {
    const ids = new Set(cartItems.map((item) => item.product.vendor_id));
    return Array.from(ids);
  }, [cartItems]);

  // Subscribe to real-time vendor delivery_hours changes
  // When a vendor updates their store hours, notify the user
  const { isConnected: hoursRealtimeConnected } = useVendorHoursRealtime({
    vendorIds: cartVendorIds,
    enabled: cartVendorIds.length > 0 && isFocused,
    onHoursChange: React.useCallback((update) => {
      console.log(`🕒 [CartsByStoreScreen] Vendor hours changed in real-time:`, {
        vendorId: update.vendorId,
        vendorName: update.vendorName,
        newHours: update.deliveryHours,
      });
      
      // Show alert to user that store hours have changed
      platformAlert(
        "Store Hours Updated",
        `${update.vendorName || 'A store'} has updated their hours. Check availability before checkout.`,
        [{ text: "OK" }]
      );
    }, []),
  });

  // Log realtime connection status for debugging
  useEffect(() => {
    if (cartVendorIds.length > 0) {
      console.log(`🔌 [CartsByStoreScreen] Vendor hours realtime:`, {
        connected: hoursRealtimeConnected,
        monitoringVendors: cartVendorIds.length,
      });
    }
  }, [hoursRealtimeConnected, cartVendorIds.length]);

  // Use ref to track current cart items for realtime callbacks
  const cartItemsRef = React.useRef(cartItems);
  React.useEffect(() => {
    cartItemsRef.current = cartItems;
  }, [cartItems]);

  // Listen for cart_items deletions in real-time (when items are removed by other devices/checkout)
  useEffect(() => {
    if (!session?.user?.id) return;

    let channel: ReturnType<typeof supabase.channel> | null = null;
    let reconnectTimeout: NodeJS.Timeout | null = null;

    const setupSubscription = () => {
      // Clean up existing channel if any
      if (channel) {
        supabase.removeChannel(channel);
      }

      console.log("[CartsByStoreScreen] Setting up realtime listener for cart deletions");
      channel = supabase
        .channel(`carts-by-store-deletions-${session.user.id}`)
        .on(
          'postgres_changes',
          {
            event: 'DELETE',
            schema: 'public',
            table: 'cart_items',
            filter: `user_id=eq.${session.user.id}`,
          },
          async (payload) => {
            console.log('[CartsByStoreScreen] Cart item deleted via realtime:', {
              deletedId: payload.old?.id,
              productId: payload.old?.product_id,
              userId: payload.old?.user_id,
              fullPayload: payload,
            });
            
            // Check if the deleted item was in our current cart using ref
            const deletedCartItemId = payload.old?.id;
            const deletedProductId = payload.old?.product_id;
            const currentCartItems = cartItemsRef.current;
            const wasInCurrentCart = currentCartItems.some(item => 
              item.id === deletedCartItemId || item.product.id === deletedProductId
            );
            
            // Immediately refresh cart to update the UI
            await refreshCart();
            
            // Show alert if the item was in the current cart
            if (wasInCurrentCart) {
              console.log('[CartsByStoreScreen] Item in cart was deleted, showing alert');
              // Small delay to ensure UI updates are complete
              setTimeout(() => {
                platformAlert(
                  "Cart Updated",
                  "An item in your cart is no longer available and has been removed.",
                  [{ text: "Okay", onPress: () => {} }]
                );
              }, 500);
            }
          }
        )
        // Also listen for product status/quantity changes (to update available quantity in cart)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'products',
          },
          (payload) => {
            const newRow: any = payload.new;
            const oldRow: any = payload.old;
            
            // Check if product quantity or status changed
            const quantityChanged = Number(newRow?.quantity ?? NaN) !== Number(oldRow?.quantity ?? NaN);
            const statusChanged = newRow?.status !== oldRow?.status;
            
            if (quantityChanged || statusChanged) {
              console.log('[CartsByStoreScreen] Product quantity/status changed via realtime:', {
                productId: newRow?.id,
                oldStatus: oldRow?.status,
                newStatus: newRow?.status,
                oldQuantity: oldRow?.quantity,
                newQuantity: newRow?.quantity,
              });
              
              // Always refresh cart when product quantity/status changes
              // This will update the displayed quantity even if product is still available
              console.log('[CartsByStoreScreen] Product updated, refreshing cart to show new quantity...');
              refreshCart();
            }
          }
        )
        .subscribe((status) => {
          console.log('[CartsByStoreScreen] Realtime subscription status:', status);
          
          if (status === 'SUBSCRIBED') {
            console.log('[CartsByStoreScreen] ✅ Successfully subscribed to cart deletions');
            // Clear any pending reconnection attempts
            if (reconnectTimeout) {
              clearTimeout(reconnectTimeout);
              reconnectTimeout = null;
            }
          } else if (status === 'CLOSED' || status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            console.warn(`[CartsByStoreScreen] ⚠️ Subscription ${status}, attempting to reconnect...`);
            // Attempt to reconnect after a short delay
            reconnectTimeout = setTimeout(() => {
              console.log('[CartsByStoreScreen] 🔄 Reconnecting realtime subscription...');
              setupSubscription();
            }, 2000);
          }
        });
    };

    // Initial setup
    setupSubscription();

    return () => {
      console.log("[CartsByStoreScreen] Cleaning up realtime listener");
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, [session?.user?.id, refreshCart]);

  // Periodic check for cart changes (fallback if realtime doesn't work)
  // Note: cartItemsRef is already defined above for realtime callbacks

  useEffect(() => {
    if (!session?.user?.id) return;

    // Check every 3 seconds if cart items still exist in database
    const checkInterval = setInterval(async () => {
      const currentCartItems = cartItemsRef.current;
      
      // Skip if cart is empty (avoid unnecessary checks)
      if (currentCartItems.length === 0) {
        return;
      }

      try {
        const { data: dbCartItems, error } = await supabase
          .from('cart_items')
          .select('id, product_id')
          .eq('user_id', session.user.id);

        if (error) {
          console.warn('[CartsByStoreScreen] Error checking cart items:', error);
          return;
        }

        const dbItemIds = new Set((dbCartItems || []).map(item => item.id));
        const displayedItemIds = new Set(currentCartItems.map(item => item.id));

        // Check if any displayed items are missing from database
        const missingItems = Array.from(displayedItemIds).filter(id => !dbItemIds.has(id));
        
        if (missingItems.length > 0) {
          console.log('[CartsByStoreScreen] 🔄 Found missing cart items via polling:', missingItems);
          refreshCart();
          return; // Exit early after refresh
        }

        // Also check if any products in cart have quantity/status changes
        // This will refresh cart to show updated quantities even if products are still available
        const productIds = Array.from(new Set(currentCartItems.map(item => item.product.id)));
        if (productIds.length > 0) {
          const { data: products, error: productsError } = await supabase
            .from('products')
            .select('id, quantity, status')
            .in('id', productIds);

          if (!productsError && products) {
            // Check if any products have changed (sold out, archived, or quantity changed)
            const hasChanges = products.some(p => {
              const cartItem = currentCartItems.find(item => item.product.id === p.id);
              if (!cartItem) return false;
              
              const cartProduct = cartItem.product;
              const quantityChanged = Number(p.quantity ?? 0) !== Number(cartProduct.quantity ?? 0);
              const statusChanged = p.status !== (cartProduct as any).status;
              
              return quantityChanged || statusChanged || 
                     p.status === 'sold' || p.status === 'archived' || Number(p.quantity ?? 0) <= 0;
            });

            if (hasChanges) {
              console.log('[CartsByStoreScreen] 🔄 Found product quantity/status changes via polling');
              refreshCart();
            }
          }
        }
      } catch (err) {
        console.warn('[CartsByStoreScreen] Error in periodic cart check:', err);
      }
    }, 3000); // Check every 3 seconds

    return () => clearInterval(checkInterval);
  }, [session?.user?.id, refreshCart]);

  // Refetch cart when screen comes into focus to catch realtime updates
  useFocusEffect(
    React.useCallback(() => {
      console.log("[CartsByStoreScreen] Screen focused, refreshing cart");
      refreshCart();
      
      // Verify realtime subscription is active when screen is focused
      if (session?.user?.id) {
        const channels = supabase.getChannels();
        const cartChannel = channels.find(ch => 
          ch.topic.includes(`carts-by-store-deletions-${session.user.id}`)
        );
        
        if (cartChannel) {
          console.log('[CartsByStoreScreen] Realtime channel found:', {
            topic: cartChannel.topic,
            state: cartChannel.state,
          });
          
          // If channel is closed, it will be reconnected by the useEffect
          if (cartChannel.state === 'closed') {
            console.log('[CartsByStoreScreen] Channel is closed, will reconnect via useEffect');
          }
        } else {
          console.log('[CartsByStoreScreen] No realtime channel found, will be created by useEffect');
        }
      }
    }, [refreshCart, session?.user?.id])
  );

  // local menu state (for Android / fallback)
  const [menuVendor, setMenuVendor] = useState<null | GroupedStore>(null);

  const stores = useMemo<GroupedStore[]>(() => {
    const map = new Map<string, GroupedStore>();

    cartItems.forEach((ci) => {
      const v = ci.product.vendor;
      const key = v.id;
      const existing = map.get(key);
      const line = v.street_line1;
      const price = ci.product.price * ci.quantity;
      const thumb = ci.product.images?.[0] ?? null;

      if (existing) {
        existing.itemsCount += ci.quantity;
        existing.subtotal += price;
        if (!existing.thumb && thumb) existing.thumb = thumb;
      } else {
        map.set(key, {
          vendorId: key,
          storeName: v.store_name,
          addressLine1: line,
          city: v.city,
          state: v.state,
          itemsCount: ci.quantity,
          subtotal: price,
          thumb,
          logo_image: (v as any).logo_image || null,
        });
      }
    });

    return Array.from(map.values());
  }, [cartItems]);

  const removeStoreCart = (vendorId: string) => {
    // собираем id элементов этого магазина и удаляем
    const ids = cartItems
      .filter((i) => i.product.vendor_id === vendorId)
      .map((i) => i.id);

    ids.forEach((id) => removeItem(id));
  };

  const confirmRemove = (store: GroupedStore) => {
    platformAlert(
      "Remove cart?",
      `Remove ${store.itemsCount} item${
        store.itemsCount !== 1 ? "s" : ""
      } from ${store.storeName}?`,
      [
        { text: "Cancel", style: "cancel" },
        {
          text: "Remove",
          style: "destructive",
          onPress: () => removeStoreCart(store.vendorId),
        },
      ]
    );
  };

  const openMenu = (store: GroupedStore) => {
    if (Platform.OS === "ios") {
      ActionSheetIOS.showActionSheetWithOptions(
        {
          options: ["Cancel", "Remove cart"],
          cancelButtonIndex: 0,
          destructiveButtonIndex: 1,
          title: store.storeName,
          message: `${store.itemsCount} item${
            store.itemsCount !== 1 ? "s" : ""
          } • ${formatPrice(store.subtotal)}`,
          userInterfaceStyle: "light",
        },
        (idx) => {
          if (idx === 1) confirmRemove(store);
        }
      );
    } else {
      setMenuVendor(store); // откроем наше модальное меню
    }
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <ScrollView contentContainerStyle={styles.content}>
        <View style={styles.welcomeSection}>
          <Text style={styles.title}>Carts By Store</Text>
        </View>

        {stores.length === 0 ? (
          <View style={styles.emptyContainer}>
            <View style={styles.emptyIconContainer}>
              <Feather name="shopping-cart" size={64} color="#FFFFFF" />
            </View>
            <Text style={styles.emptyTitle}>Your cart is empty</Text>
            <Text style={styles.emptyText}>
              Add items to see stores here.
            </Text>
            <TouchableOpacity 
              style={styles.startShoppingBtn}
              onPress={() => nav.navigate('Main' as any)}
            >
              <Text style={styles.startShoppingText}>Start Shopping</Text>
            </TouchableOpacity>
          </View>
        ) : (
          stores.map((s) => (
            <View
              key={s.vendorId}
              style={[styles.card, { borderColor: colors.border }]}
            >
              <View style={styles.headerRow}>
                <Image
                  source={
                    s.logo_image
                      ? { uri: s.logo_image }
                      : {
                          uri: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVQIW2P4//8/AwAI/AL+5bXjAwAAAABJRU5ErkJggg==",
                        }
                  }
                  style={styles.logo}
                />
                <View style={styles.headerText}>
                  <Text style={styles.storeName}>{s.storeName}</Text>
                  <Text style={styles.meta}>
                    {s.itemsCount} item{s.itemsCount !== 1 ? "s" : ""} •{" "}
                    {formatPrice(s.subtotal)}
                  </Text>
                  {!!formattedAddress && (
                    <Text style={styles.deliveryTo} numberOfLines={1}>
                      Deliver to {formattedAddress}
                    </Text>
                  )}
                </View>

                <TouchableOpacity
                  style={styles.menuBtn}
                  hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                  onPress={() => openMenu(s)}
                >
                  <Feather
                    name="more-horizontal"
                    size={20}
                    color={colors.textLight}
                  />
                </TouchableOpacity>
              </View>

              <TouchableOpacity
                style={styles.primaryBtn}
                onPress={() => nav.navigate("Cart", { vendorId: s.vendorId })}
              >
                <Text style={styles.primaryBtnText}>View cart</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.secondaryBtn}
                onPress={() => nav.navigate("Vendor", { id: s.vendorId })}
              >
                <Text style={styles.secondaryBtnText}>View store</Text>
              </TouchableOpacity>
            </View>
          ))
        )}
      </ScrollView>

      {/* ANDROID / fallback menu */}
      <Modal
        transparent
        visible={!!menuVendor && Platform.OS !== "ios"}
        animationType="fade"
        onRequestClose={() => setMenuVendor(null)}
      >
        <View style={styles.webModalWrapper}>
          <Pressable style={styles.backdrop} onPress={() => setMenuVendor(null)}>
            <View />
          </Pressable>

          <View style={styles.sheet}>
            <Text style={styles.sheetTitle}>{menuVendor?.storeName}</Text>
            <Text style={styles.sheetSubtitle}>
              {menuVendor?.itemsCount} item
              {menuVendor && menuVendor.itemsCount !== 1 ? "s" : ""} •{" "}
              {formatPrice(menuVendor?.subtotal || 0)}
            </Text>

            <Pressable
              style={styles.sheetBtnDestructive}
              onPress={() => {
                if (menuVendor) confirmRemove(menuVendor);
                setMenuVendor(null);
              }}
            >
              <Text style={styles.sheetBtnDestructiveText}>Remove cart</Text>
            </Pressable>

            <Pressable
              style={styles.sheetBtn}
              onPress={() => setMenuVendor(null)}
            >
              <Text style={styles.sheetBtnText}>Cancel</Text>
            </Pressable>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const PHONE_CONTAINER_MAX_WIDTH = 430;

const createStyles = (colors: any) =>
  StyleSheet.create({
    container: { flex: 1, backgroundColor: colors.background },
    content: { 
      flexGrow: 1,
      paddingBottom: spacing.xl,
    },
    welcomeSection: {
      paddingLeft: spacing.lg + 4,
      paddingRight: spacing.lg,
      paddingTop: spacing.sm,
      paddingBottom: spacing.md,
    },
    title: { 
      fontSize: 28, 
      fontWeight: "800", 
      color: colors.text,
      letterSpacing: -0.5,
    },
    emptyContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: spacing.xl,
      marginTop: -spacing.xxl,
    },
    emptyIconContainer: {
      width: 120,
      height: 120,
      borderRadius: 60,
      backgroundColor: "#000000",
      justifyContent: 'center',
      alignItems: 'center',
      marginBottom: spacing.lg,
    },
    emptyTitle: {
      fontSize: 22,
      fontWeight: "700",
      color: colors.text,
      marginBottom: spacing.xs,
      textAlign: 'center',
    },
    emptyText: {
      fontSize: 16,
      color: colors.textLight,
      textAlign: 'center',
      lineHeight: 22,
      paddingHorizontal: spacing.md,
      marginBottom: spacing.md,
    },
    startShoppingBtn: {
      backgroundColor: colors.primary,
      paddingHorizontal: spacing.xl,
      paddingVertical: 16,
      borderRadius: 28,
      shadowColor: colors.primary,
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.2,
      shadowRadius: 8,
      elevation: 4,
    },
    startShoppingText: {
      color: "#000000",
      fontSize: 16,
      fontWeight: "700",
    },

    card: {
      borderWidth: 1,
      borderRadius: 16,
      padding: 12,
      marginBottom: 16,
      marginHorizontal: spacing.md,
      backgroundColor: colors.surface,
    },
    headerRow: { flexDirection: "row", alignItems: "center" },
    logo: {
      width: 54,
      height: 54,
      borderRadius: 27,
      backgroundColor: colors.backgroundSecondary,
    },
    headerText: { flex: 1, marginLeft: 12 },
    storeName: { fontSize: 18, fontWeight: "700", color: colors.text },
    meta: { fontSize: 14, color: colors.textLight, marginTop: 2 },
    deliveryTo: { fontSize: 13, color: colors.textLight, marginTop: 2 },
    menuBtn: { marginLeft: 8 },

    primaryBtn: {
      marginTop: 12,
      backgroundColor: colors.primary,
      borderRadius: 12,
      paddingVertical: 14,
      alignItems: "center",
    },
    primaryBtnText: { color: colors.surface, fontWeight: "700", fontSize: 16 },

    secondaryBtn: {
      marginTop: 10,
      backgroundColor: colors.backgroundSecondary,
      borderRadius: 12,
      paddingVertical: 14,
      alignItems: "center",
    },
    secondaryBtnText: { color: colors.text, fontWeight: "600", fontSize: 16 },

    // modal styles
    webModalWrapper: {
      flex: 1,
      ...(Platform.OS === "web"
        ? {
            maxWidth: PHONE_CONTAINER_MAX_WIDTH,
            alignSelf: "center",
            width: "100%",
          }
        : {}),
    },
    backdrop: { flex: 1, backgroundColor: "rgba(0,0,0,0.25)" },
    sheet: {
      position: "absolute",
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: colors.surface,
      borderTopLeftRadius: 16,
      borderTopRightRadius: 16,
      padding: 16,
    },
    sheetTitle: { fontSize: 16, fontWeight: "700", color: colors.text },
    sheetSubtitle: {
      fontSize: 13,
      color: colors.textLight,
      marginTop: 2,
      marginBottom: 10,
    },
    sheetBtnDestructive: {
      backgroundColor: colors.errorBackground,
      borderRadius: 12,
      paddingVertical: 14,
      alignItems: "center",
      marginBottom: 8,
    },
    sheetBtnDestructiveText: {
      color: colors.error,
      fontWeight: "700",
      fontSize: 16,
    },
    sheetBtn: {
      backgroundColor: colors.backgroundSecondary,
      borderRadius: 12,
      paddingVertical: 14,
      alignItems: "center",
    },
    sheetBtnText: { color: colors.text, fontWeight: "600", fontSize: 16 },
  });
